import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Alert, AlertDescription } from "../ui/alert";
import { Loader2, Save, AlertCircle, CheckCircle2, BookOpen, Lightbulb, FileText } from "lucide-react";
import { ScrollArea } from "../ui/scroll-area";
import './MaturityDefinitionsDialog.css';

const MaturityDefinitionsDialog = ({ open, onOpenChange, uniqueId }) => {
    const [maturityData, setMaturityData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        if (open && uniqueId) {
            fetchMaturityData();
        }
    }, [open, uniqueId]);

    const fetchMaturityData = async () => {
        if (!uniqueId) {
            setError("No unique ID found. Please upload a file first.");
            return;
        }
        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const timestamp = new Date().getTime();
            const response = await fetch(`https://webappapikpsgpt.azurewebsites.net/get_editable_fields/${uniqueId}?t=${timestamp}`);
            if (!response.ok) {
                throw new Error("Failed to fetch editable fields");
            }
            const result = await response.json();
            const maturityItems = result.editable_fields?.data_maturity || [];
            console.log("Loaded maturity data:", maturityItems.length, "items");
            setMaturityData(maturityItems);
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : "Failed to load maturity principles";
            console.error("Error fetching maturity data:", err);
            setError(errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePrincipleChange = (index, value) => {
        setMaturityData((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], maturity_guiding_principle: value };
            return updated;
        });
    };

    const handleSave = async () => {
        if (!uniqueId) {
            setError("No unique ID found. Cannot save edits.");
            return;
        }
        setIsSaving(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const processMap = new Map();
            maturityData.forEach((item) => {
                const key = `${item.section}|||${item.process}|||${item.question}`;
                processMap.set(key, {
                    section: item.section,
                    process: item.process,
                    sub_process: item.sub_process || null,
                    question: item.question || null,
                    field: "maturity_guiding_principle",
                    value: item.maturity_guiding_principle,
                });
            });
            const edits = Array.from(processMap.values());
            console.log("Saving edits:", edits);
            const saveResponse = await fetch("https://webappapikpsgpt.azurewebsites.net/save_edits", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    unique_id: uniqueId,
                    edits: edits,
                }),
            });
            if (!saveResponse.ok) {
                const errorData = await saveResponse.json();
                throw new Error(errorData.error || "Failed to save maturity edits");
            }
            const saveResult = await saveResponse.json();
            console.log("✓ Save successful:", saveResult);
            const summary = saveResult.edit_results?.summary;
            if (summary) {
                setSuccessMessage(`Edits saved successfully!\n\nSuccessful: ${summary.successful}/${summary.total_edits}\nSuccess Rate: ${summary.success_rate}\n\nClick "Regenerate Dashboard" to see changes in the report.`);
            } else {
                setSuccessMessage("Edits saved successfully! Regenerate the dashboard to see changes.");
            }
            setTimeout(() => {
                onOpenChange(false);
            }, 2000);
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : "Failed to save edits";
            console.error("Save error:", err);
            setError(errorMsg);
        } finally {
            setIsSaving(false);
        }
    };

    const groupedByProcess = maturityData.reduce((acc, item) => {
        const key = item.process;
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(item);
        return acc;
    }, {});

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="maturity-dialog">
                <DialogHeader>
                    <div className="maturity-icon-wrapper">
                        <BookOpen className="maturity-header-icon" />
                    </div>
                    <DialogTitle className="maturity-dialog-title">
                        Edit Maturity Guiding Principles
                    </DialogTitle>
                    <DialogDescription className="maturity-dialog-desc">
                        Update the maturity guiding principles for each process. These principles guide the assessment of technical, data, and organizational readiness.
                    </DialogDescription>
                </DialogHeader>

                <div className="maturity-main-content">
                    {error && (
                        <Alert className="maturity-alert maturity-alert-error">
                            <AlertCircle className="maturity-alert-icon" size={18} />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {successMessage && (
                        <Alert className="maturity-alert maturity-alert-success">
                            <CheckCircle2 className="maturity-alert-icon" size={18} />
                            <AlertDescription className="maturity-alert-message">
                                {successMessage}
                            </AlertDescription>
                        </Alert>
                    )}

                    {isLoading ? (
                        <div className="maturity-loading">
                            <Loader2 className="maturity-loading-spinner" />
                            <span className="maturity-loading-text">
                                Loading maturity principles...
                            </span>
                        </div>
                    ) : maturityData.length === 0 ? (
                        <div className="maturity-empty">
                            <FileText className="maturity-empty-icon" />
                            <p className="maturity-empty-text">
                                No maturity principles found. Please generate the dashboard first.
                            </p>
                        </div>
                    ) : (
                        <ScrollArea className="maturity-scroll-area">
                            <div className="maturity-content-inner">
                                {Object.entries(groupedByProcess).map(([processName, items]) => (
                                    <div key={processName} className="maturity-process-group">
                                        <div className="maturity-process-header">
                                            <div className="maturity-process-icon">
                                                <Lightbulb size={18} />
                                            </div>
                                            <h3 className="maturity-process-title">{processName}</h3>
                                            <span className="maturity-process-count">{items.length}</span>
                                        </div>

                                        <div className="maturity-items-list">
                                            {items.map((item) => {
                                                const globalIndex = maturityData.indexOf(item);
                                                return (
                                                    <div key={globalIndex} className="maturity-item">
                                                        {item.sub_process && (
                                                            <div className="maturity-meta">
                                                                <span className="maturity-meta-label">Sub-process:</span>
                                                                <span className="maturity-meta-value">{item.sub_process}</span>
                                                            </div>
                                                        )}
                                                        {item.question && (
                                                            <div className="maturity-meta">
                                                                <span className="maturity-meta-label">Question:</span>
                                                                <span className="maturity-meta-value">{item.question}</span>
                                                            </div>
                                                        )}
                                                        <Label htmlFor={`principle-${globalIndex}`} className="maturity-item-label">
                                                            Maturity Guiding Principle
                                                        </Label>
                                                        <Textarea
                                                            id={`principle-${globalIndex}`}
                                                            value={item.maturity_guiding_principle}
                                                            onChange={(e) => handlePrincipleChange(globalIndex, e.target.value)}
                                                            rows={4}
                                                            className="maturity-textarea"
                                                            placeholder="Enter the maturity guiding principle..."
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </ScrollArea>
                    )}
                </div>

                <DialogFooter className="maturity-dialog-footer">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving} className="maturity-btn-cancel">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving || maturityData.length === 0} className="maturity-btn-save">
                        {isSaving ? (
                            <>
                                <Loader2 className="maturity-btn-icon maturity-btn-spinner" size={16} />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="maturity-btn-icon" size={16} />
                                Save Changes
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default MaturityDefinitionsDialog;